import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { sportsApi } from '../lib/api';
import MaraSportsLogo from '../components/MaraSportsLogo';
import {
    ArrowLeft, Users, Trophy, Shield, Calendar, MapPin,
    ChevronRight, Loader2, Star, TrendingUp
} from 'lucide-react';

const SportDetailPage = () => {
    const { sportName } = useParams();
    const navigate = useNavigate();
    const [sportData, setSportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadSportData = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await sportsApi.getDetails(sportName);
                setSportData(response.data);
            } catch (err) {
                console.error('Failed to load sport details:', err);
                setError('Sport not found or failed to load');
            } finally {
                setLoading(false);
            }
        };
        loadSportData();
    }, [sportName]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    if (error || !sportData) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex flex-col items-center justify-center text-white">
                <h1 className="text-2xl font-bold mb-4">Sport Not Found</h1>
                <p className="text-muted-foreground mb-6">{error}</p>
                <Button onClick={() => navigate('/')} variant="outline">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Home
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-3">
                            <MaraSportsLogo className="h-12" />
                        </Link>
                        <Button
                            variant="ghost"
                            onClick={() => navigate('/')}
                            className="text-muted-foreground hover:text-white"
                            data-testid="back-home-btn"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Home
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-24 pb-12 px-6 md:px-12">
                <div className="max-w-[1400px] mx-auto">
                    <div className="mb-8">
                        <p className="text-xs tracking-widest uppercase text-primary mb-2">Sport Details</p>
                        <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter uppercase" data-testid="sport-title">
                            {sportData.sport}
                        </h1>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                        <div className="bg-[#0d2847] border border-white/5 p-6" data-testid="stat-total-athletes">
                            <Users className="w-6 h-6 text-primary mb-3" />
                            <p className="stat-display text-3xl text-white">{sportData.stats.total_athletes}</p>
                            <p className="text-xs tracking-widest uppercase text-muted-foreground mt-1">Total Athletes</p>
                        </div>
                        <div className="bg-[#0d2847] border border-white/5 p-6" data-testid="stat-verified">
                            <Shield className="w-6 h-6 text-success mb-3" />
                            <p className="stat-display text-3xl text-white">{sportData.stats.verified_athletes}</p>
                            <p className="text-xs tracking-widest uppercase text-muted-foreground mt-1">Verified</p>
                        </div>
                        <div className="bg-[#0d2847] border border-white/5 p-6" data-testid="stat-tournaments">
                            <Trophy className="w-6 h-6 text-accent mb-3" />
                            <p className="stat-display text-3xl text-white">{sportData.stats.total_tournaments}</p>
                            <p className="text-xs tracking-widest uppercase text-muted-foreground mt-1">Tournaments</p>
                        </div>
                        <div className="bg-[#0d2847] border border-white/5 p-6" data-testid="stat-active">
                            <Calendar className="w-6 h-6 text-secondary mb-3" />
                            <p className="stat-display text-3xl text-white">{sportData.stats.active_tournaments}</p>
                            <p className="text-xs tracking-widest uppercase text-muted-foreground mt-1">Active Now</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Top Athletes Section */}
            {sportData.top_athletes && sportData.top_athletes.length > 0 && (
                <section className="py-12 px-6 md:px-12 bg-[#0d2847]">
                    <div className="max-w-[1400px] mx-auto">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <p className="text-xs tracking-widest uppercase text-accent mb-2">Featured</p>
                                <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight uppercase">
                                    Top Verified Athletes
                                </h2>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {sportData.top_athletes.map((athlete, index) => (
                                <div
                                    key={athlete.id}
                                    className="bg-[#0a1628] border border-white/5 p-6 card-hover cursor-pointer"
                                    onClick={() => navigate(`/athletes/${athlete.id}`)}
                                    data-testid={`top-athlete-${index}`}
                                >
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                                            <Star className="w-6 h-6 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-sm truncate">{athlete.full_name}</h3>
                                            <p className="text-xs text-muted-foreground">{athlete.country}</p>
                                        </div>
                                    </div>
                                    {athlete.school && (
                                        <p className="text-xs text-muted-foreground truncate">{athlete.school}</p>
                                    )}
                                    {athlete.verified && (
                                        <div className="flex items-center gap-1 mt-2 text-success">
                                            <Shield className="w-3 h-3" />
                                            <span className="text-xs">Verified</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* All Athletes Section */}
            <section className="py-12 px-6 md:px-12">
                <div className="max-w-[1400px] mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <p className="text-xs tracking-widest uppercase text-primary mb-2">Athletes</p>
                            <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight uppercase">
                                Registered Athletes
                            </h2>
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => navigate(`/talent?sport=${encodeURIComponent(sportData.sport)}`)}
                            className="border-white/20 hover:border-white text-sm"
                            data-testid="view-all-athletes-btn"
                        >
                            View All
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                    {sportData.athletes && sportData.athletes.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {sportData.athletes.slice(0, 8).map((athlete, index) => (
                                <div
                                    key={athlete.id}
                                    className="bg-[#0d2847] border border-white/5 p-5 card-hover cursor-pointer"
                                    onClick={() => navigate(`/athletes/${athlete.id}`)}
                                    data-testid={`athlete-card-${index}`}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-sm truncate">{athlete.full_name}</h3>
                                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                                <MapPin className="w-3 h-3" />
                                                {athlete.country}
                                            </p>
                                        </div>
                                        {athlete.verified && (
                                            <Shield className="w-4 h-4 text-success flex-shrink-0" />
                                        )}
                                    </div>
                                    {athlete.school && (
                                        <p className="text-xs text-muted-foreground truncate mb-2">{athlete.school}</p>
                                    )}
                                    {athlete.age_category && (
                                        <span className="inline-block px-2 py-1 bg-white/5 text-xs">
                                            {athlete.age_category}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-[#0d2847] border border-white/5">
                            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">No athletes registered yet for this sport</p>
                            <Button
                                onClick={() => navigate('/auth?mode=register')}
                                className="mt-4 bg-primary text-white"
                            >
                                Be the First to Register
                            </Button>
                        </div>
                    )}
                </div>
            </section>

            {/* Tournaments Section */}
            <section className="py-12 px-6 md:px-12 bg-[#0d2847]">
                <div className="max-w-[1400px] mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <p className="text-xs tracking-widest uppercase text-accent mb-2">Competitions</p>
                            <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight uppercase">
                                Tournaments
                            </h2>
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => navigate(`/tournaments?sport=${encodeURIComponent(sportData.sport)}`)}
                            className="border-white/20 hover:border-white text-sm"
                            data-testid="view-all-tournaments-btn"
                        >
                            View All
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                    {sportData.tournaments && sportData.tournaments.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {sportData.tournaments.map((tournament, index) => (
                                <div
                                    key={tournament.id}
                                    className="bg-[#0a1628] border border-white/5 p-6 card-hover cursor-pointer"
                                    onClick={() => navigate(`/tournaments/${tournament.id}`)}
                                    data-testid={`tournament-card-${index}`}
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <Trophy className="w-8 h-8 text-accent" />
                                        <span className={`px-2 py-1 text-xs uppercase tracking-wider ${
                                            tournament.tournament_status === 'in_progress' 
                                                ? 'bg-success/20 text-success' 
                                                : tournament.tournament_status === 'registration_open'
                                                    ? 'bg-primary/20 text-primary'
                                                    : 'bg-white/10 text-muted-foreground'
                                        }`}>
                                            {tournament.tournament_status?.replace('_', ' ')}
                                        </span>
                                    </div>
                                    <h3 className="font-bold text-lg mb-2">{tournament.name}</h3>
                                    <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                                        <MapPin className="w-3 h-3" />
                                        {tournament.location}
                                    </p>
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {tournament.start_date}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-[#0a1628] border border-white/5">
                            <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">No tournaments available yet for this sport</p>
                            <Button
                                onClick={() => navigate('/tournaments')}
                                className="mt-4 bg-accent text-black"
                            >
                                Browse All Tournaments
                            </Button>
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 px-6 md:px-12">
                <div className="max-w-[1400px] mx-auto text-center">
                    <h2 className="font-heading font-bold text-2xl md:text-4xl tracking-tight uppercase mb-6">
                        Ready to Compete in {sportData.sport}?
                    </h2>
                    <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                        Create your athlete profile, join tournaments, and showcase your talent to scouts worldwide.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <Button
                            onClick={() => navigate('/auth?mode=register')}
                            className="bg-primary hover:bg-cyan-400 text-[#0a1628] uppercase tracking-widest text-sm font-bold h-12 px-8"
                            data-testid="cta-register-btn"
                        >
                            Register Now
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/talent')}
                            className="border-white/20 hover:border-white uppercase tracking-widest text-sm font-bold h-12 px-8"
                            data-testid="cta-explore-btn"
                        >
                            Explore Talent
                        </Button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-white/5 py-8">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <MaraSportsLogo className="h-8" />
                        <p className="text-xs text-muted-foreground">
                            © 2026 NextGen Tournaments. The Digital Operating System for Youth Sports.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default SportDetailPage;
